import React, { useState, useContext, useEffect } from "react";
import * as HomeStyle from "./HomeStyle";
import { useNavigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import {
  FaArrowLeft,
  FaArrowRight,
  FaHourglassHalf,
  FaCheckCircle,
  FaFileSignature,
  FaFileInvoiceDollar,
  FaUsers,
  FaCalendarWeek,
  FaCalendarAlt,
  FaHeadset,
  FaSearchDollar,
} from "react-icons/fa";
import { AuthContext } from "../../Context/AuthContext";
import { platformService } from "../../Services/platformService";
import { auditService } from "../../Services/auditService";
import adminAxiosInstance from "../../Services/api/adminAxiosInstance";

const menuOptions = {
  CLIENTES: {
    icon: "./clientes.png",
    sublinks: [
      { label: "REDEFINIR LOGIN", to: "/redefiniremail" },
      { label: "TODOS OS CLIENTES", to: "/clientes" },
      { label: "VALIDAR DOC (CPF/CNPJ)", to: "/validar-doc" },
    ],
  },
  FERRAMENTAS: {
    icon: "./tools.png",
    sublinks: [
      { label: "ADICIONAR INDICAÇÃO", to: "/indicacao" },
      { label: "ADICIONAR PLUS", to: "/plusAnual" },
      { label: "CRIAR MENSAGEM", to: "/mensagem" },
      { label: "CRIAR NOTÍCIA", to: "/noticias" },
      { label: "ANTECIPAR LUCRO", to: "/anteciparLucro" },
    ],
  },
  CONSULTORES: {
    icon: "./staff-icon.png",
    directLink: "/consultores",
  },
  CONTRATOS: {
    icon: "./contratos.png",
    sublinks: [
      { label: "VALIDAR CONTRATOS", to: "/depositos" },
      { label: "VER CONTRATOS", to: "/contratos" },
    ],
  },
  SAQUES: {
    icon: "./saque.png",
    sublinks: [
      { label: "VALIDAR SAQUES", to: "/saques" },
      { label: "VER SAQUES", to: "/saquesFeitos" },
    ],
  },
  AUDITORIA: {
    icon: "./audit-icon.png",
    directLink: "/auditoria",
  },
};

const formatCurrency = (value) => {
  if (typeof value !== "number") return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export default function Home() {
  const [opSelected, setOpSelected] = useState(null);
  const navigate = useNavigate();
  const { authState } = useContext(AuthContext);
  const [reportData, setReportData] = useState(null);
  const [loginStats, setLoginStats] = useState({ today: 0, week: 0, month: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSupportLoading, setIsSupportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const handleSupportAccess = async () => {
    setIsSupportLoading(true);
    try {
      const response = await adminAxiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}auth/support-access`
      );
      const { payload, signature } = response.data;
      const payloadStr = encodeURIComponent(JSON.stringify(payload));
      const ssoUrl = `https://help.ccypher.com.br/sso?payload=${payloadStr}&signature=${signature}`;
      window.open(ssoUrl, "_blank");
    } catch (err) {
      alert("Erro ao conectar ao suporte.");
    } finally {
      setIsSupportLoading(false);
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!authState.isAuthenticated) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const startOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        const endOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );

        const auditParams = {
          startDate: startOfMonth.toISOString().split("T")[0],
          endDate: endOfMonth.toISOString().split("T")[0],
          limit: 5000,
        };

        const [financialData, auditResponse] = await Promise.all([
          platformService.getMonthlyReport(currentDate),
          auditService.searchClientAudits(auditParams),
        ]);

        setReportData(financialData);

        const audits = auditResponse.audits || [];
        const now = new Date();
        const startOfToday = new Date(now.setHours(0, 0, 0, 0));
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());

        const uniqueToday = new Set();
        const uniqueWeek = new Set();
        const uniqueMonth = new Set();

        audits.forEach((audit) => {
          if (!audit.ipAddress) return;
          const auditDate = new Date(audit.dateCreated);
          uniqueMonth.add(audit.ipAddress);
          if (auditDate >= startOfToday) uniqueToday.add(audit.ipAddress);
          if (auditDate >= startOfWeek) uniqueWeek.add(audit.ipAddress);
        });

        setLoginStats({
          today: uniqueToday.size,
          week: uniqueWeek.size,
          month: uniqueMonth.size,
        });
      } catch (err) {
        setError("Erro ao carregar dashboard.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, [currentDate, authState.isAuthenticated]);

  const SkeletonCard = () => <HomeStyle.StatCard className="skeleton" />;

  return (
    <>
      <HomeStyle.HomeContainer>
        <HomeStyle.DashboardContainer>
          <HomeStyle.DashboardHeader>
            <div className="header-titles">
              <h2>Central de Dados</h2>
            </div>

            <div className="header-actions">
              <HomeStyle.SupportButton
                onClick={() => navigate("/auditoria-contratos")}
                style={{ background: "#2980b9" }}
              >
                <FaSearchDollar />
                Auditoria Contratos
              </HomeStyle.SupportButton>

              <HomeStyle.SupportButton
                onClick={handleSupportAccess}
                disabled={isSupportLoading}
              >
                <FaHeadset />
                {isSupportLoading ? "Conectando..." : "C-Cypher Suporte"}
              </HomeStyle.SupportButton>

              <HomeStyle.MonthNavigator>
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() - 1,
                        1
                      )
                    )
                  }
                >
                  <FaArrowLeft />
                </button>
                <span>
                  {currentDate.toLocaleString("pt-BR", {
                    month: "long",
                    year: "numeric",
                  })}
                </span>
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth() + 1,
                        1
                      )
                    )
                  }
                >
                  <FaArrowRight />
                </button>
              </HomeStyle.MonthNavigator>
            </div>
          </HomeStyle.DashboardHeader>

          <HomeStyle.DashboardGrid>
            {isLoading ? (
              [...Array(7)].map((_, i) => <SkeletonCard key={i} />)
            ) : error ? (
              <HomeStyle.ErrorState>{error}</HomeStyle.ErrorState>
            ) : (
              <>
                <HomeStyle.StatCard color="#CFA55A">
                  <FaUsers className="icon" />
                  <span className="label">Hoje</span>
                  <span className="value">{loginStats.today}</span>
                </HomeStyle.StatCard>
                <HomeStyle.StatCard color="#CFA55A">
                  <FaCalendarWeek className="icon" />
                  <span className="label">Semana</span>
                  <span className="value">{loginStats.week}</span>
                </HomeStyle.StatCard>
                <HomeStyle.StatCard color="#CFA55A">
                  <FaCalendarAlt className="icon" />
                  <span className="label">Mês</span>
                  <span className="value">{loginStats.month}</span>
                </HomeStyle.StatCard>
                <HomeStyle.StatCard color="#f39c12">
                  <FaHourglassHalf className="icon" />
                  <span className="label">Saques Pendentes</span>
                  <span className="value">
                    {formatCurrency(reportData?.totalPendingWithdrawsInMonth)}
                  </span>
                </HomeStyle.StatCard>
                <HomeStyle.StatCard color="#27ae60">
                  <FaCheckCircle className="icon" />
                  <span className="label">Saques Pagos</span>
                  <span className="value">
                    {formatCurrency(reportData?.totalPaidWithdrawsInMonth)}
                  </span>
                </HomeStyle.StatCard>
                <HomeStyle.StatCard color="#2980b9">
                  <FaFileSignature className="icon" />
                  <span className="label">Contratos Pend.</span>
                  <span className="value">
                    {formatCurrency(reportData?.totalPendingContractsInMonth)}
                  </span>
                </HomeStyle.StatCard>
                <HomeStyle.StatCard color="#8e44ad">
                  <FaFileInvoiceDollar className="icon" />
                  <span className="label">Contratos Ativos</span>
                  <span className="value">
                    {formatCurrency(reportData?.totalActiveContractsInMonth)}
                  </span>
                </HomeStyle.StatCard>
              </>
            )}
          </HomeStyle.DashboardGrid>
        </HomeStyle.DashboardContainer>

        <HomeStyle.HomeGrid>
          {Object.entries(menuOptions).map(([name, data]) => (
            <HomeStyle.OptionCard
              key={name}
              onClick={() =>
                data.directLink
                  ? navigate(data.directLink)
                  : setOpSelected(name)
              }
            >
              <div className="iconBox">
                <img src={data.icon} alt={name} />
              </div>
              <span className="name">{name}</span>
            </HomeStyle.OptionCard>
          ))}
        </HomeStyle.HomeGrid>
      </HomeStyle.HomeContainer>

      {opSelected && (
        <HomeStyle.SubMenuOverlay>
          <HomeStyle.CloseButton onClick={() => setOpSelected(null)}>
            <IoClose />
          </HomeStyle.CloseButton>
          <HomeStyle.SubMenuContainer>
            <h2>{opSelected}</h2>
            {menuOptions[opSelected].sublinks.map((link) => (
              <HomeStyle.StyledLink
                key={link.to}
                to={link.to}
                onClick={() => setOpSelected(null)}
              >
                <HomeStyle.SubMenuOption>{link.label}</HomeStyle.SubMenuOption>
              </HomeStyle.StyledLink>
            ))}
          </HomeStyle.SubMenuContainer>
        </HomeStyle.SubMenuOverlay>
      )}
    </>
  );
}
